import useSubscriptions from '~/composable/checkout/validations/useSubscriptions'
import { VALIDATION_STATUS } from '~/constants/ValidationStatus'
import useTlmCart from '~/composable/useTlmCart'
import useCartQueries from '~/composable/useCartQueries'
import { CUSTOMER_SUBSCRIPTION_TYPE, SECRET_NUMBER_TYPE } from '~/constants/CheckoutFlow'
import {
  checkoutDebug,
  isCheckoutMockOrderValidateEnabled
} from '~/composable/checkout/useCheckoutDebug'
import { useFlowValidationStore } from '~/stores/useFlowValidationStore'

function parsePersistedAnumber (raw) {
  try {
    return JSON.parse(raw)
  } catch (_err) {
    return null
  }
}

function getPersistedMobileNumberIdFromStorage (phoneNumber) {
  const raw = window?.localStorage?.getItem('tlm_last_selected_anumber')
  if (!raw) {
    return null
  }
  const parsed = parsePersistedAnumber(raw)
  if (!parsed?.mobileNumberId || !parsed?.phoneNumber) {
    return null
  }
  if (String(parsed.phoneNumber) !== String(phoneNumber)) {
    return null
  }
  return parsed.mobileNumberId
}

function getPersistedMobileNumberId (phoneNumber) {
  if (!process.client || !phoneNumber) {
    return null
  }
  return getPersistedMobileNumberIdFromStorage(phoneNumber)
}

function resolvePhoneNumber (numberInfo, subscription, cartPhoneNumber) {
  return numberInfo?.value?.mobileNumber ??
    cartPhoneNumber ??
    subscription?.newNumber?.numberSelected?.phoneNumber ??
    subscription?.moveNumber?.mobileNumber ??
    null
}

function resolveNewNumberMobileNumberId (numberInfo, subscription, phoneNumber) {
  const fromNumberInfo = numberInfo?.value?.mobileNumberId
  if (fromNumberInfo) {
    return fromNumberInfo
  }
  const fromSelection = subscription?.newNumber?.numberSelected?.id
  if (fromSelection) {
    return fromSelection
  }
  return getPersistedMobileNumberId(phoneNumber)
}

function resolveMobileNumberId (isNewNumber, numberInfo, subscription, phoneNumber) {
  if (!isNewNumber) {
    return numberInfo?.value?.mobileNumberId ?? null
  }
  return resolveNewNumberMobileNumberId(numberInfo, subscription, phoneNumber)
}

function applyMoveNumberCartFields (built, numberInfo, getItemAttributesForSubscriptionFromCart) {
  built.simcardNumber = numberInfo?.value?.simcardNumber ?? getItemAttributesForSubscriptionFromCart('ICC')
  built.cpsCode = numberInfo?.value?.cpsCode ?? getItemAttributesForSubscriptionFromCart('CPSCode')
  built.initiateNpASAP = numberInfo?.value?.initiateNpASAP ??
    (getItemAttributesForSubscriptionFromCart('IsImmediateNpRequest') === 'true')
}

function buildSecretNumberFields (numberInfo, getItemAttributesForSubscriptionFromCart) {
  const secretInfoType = numberInfo?.value?.secretInfoType ??
    getItemAttributesForSubscriptionFromCart('SecretInfoType')
  return {
    simcardType: numberInfo?.value?.simcardType ?? getItemAttributesForSubscriptionFromCart('SIMCardType'),
    secretNumber: numberInfo?.value?.secretNumber ?? (secretInfoType !== SECRET_NUMBER_TYPE.SECRET_NONE),
    secretInfoType
  }
}

function createNumberInfoPayload ({
  numberInfo,
  subscription,
  phoneNumber,
  isNewNumber,
  isMoveNumber,
  isNumberPorting,
  subscriptionList,
  getItemAttributesForSubscriptionFromCart
}) {
  const built = {
    ...(numberInfo?.value || {}),
    ...buildSecretNumberFields(numberInfo, getItemAttributesForSubscriptionFromCart),
    mobileNumber: phoneNumber,
    mobileNumberId: resolveMobileNumberId(isNewNumber, numberInfo, subscription, phoneNumber),
    np: isMoveNumber || isNumberPorting(subscriptionList.value)
  }
  if (isMoveNumber) {
    applyMoveNumberCartFields(built, numberInfo, getItemAttributesForSubscriptionFromCart)
  }
  return built
}

function buildValidateNumberInfo (state, appCtx) {
  const { subscription, numberInfo } = state
  const {
    getItemAttributesForSubscriptionFromCart,
    isNumberPorting,
    subscriptionList
  } = useTlmCart(appCtx)
  const menuType = subscription?.menu?.value
  const cartPhoneNumber = getItemAttributesForSubscriptionFromCart('PhoneNumber')
  const mobileNumber = resolvePhoneNumber(numberInfo, subscription, cartPhoneNumber)

  if (!mobileNumber && !numberInfo?.value) {
    return null
  }

  return createNumberInfoPayload({
    numberInfo,
    subscription,
    phoneNumber: mobileNumber || cartPhoneNumber,
    isNewNumber: menuType === CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER,
    isMoveNumber: menuType === CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER,
    isNumberPorting,
    subscriptionList,
    getItemAttributesForSubscriptionFromCart
  })
}

export function buildPrePaymentSignupPayload (state, appCtx, checkoutFlow) {
  const { customerInfo } = state
  const { subscriptionList, installments, mixPoList, cart } = useTlmCart(appCtx)
  const { cartOnlyContainsSubscription } = useCartQueries(appCtx, cart)
  const isSimOnly = cartOnlyContainsSubscription.value
  let shopReservationDTO = null
  if (!isSimOnly) {
    shopReservationDTO = { installments: installments.value }
  }

  const payload = {
    ...customerInfo.subscriptionSignUpData.value,
    isSimOnly,
    bundleId: subscriptionList.value.sku,
    mixServices: mixPoList.value,
    checkoutFlow,
    shopReservationDTO
  }
  const numberInfoPayload = buildValidateNumberInfo(state, appCtx)
  if (numberInfoPayload) {
    payload.numberInfo = numberInfoPayload
    payload.simcardType = numberInfoPayload.simcardType
  }
  return payload
}

export function extractValidationErrorCodes (validationResult) {
  if (!validationResult) {
    return []
  }
  const codes = new Set()
  const fields = ['errors', 'actionErrors', 'signupBlockingNotifications']
  fields.forEach((field) => {
    const list = validationResult[field]
    if (!Array.isArray(list)) {
      return
    }
    list.forEach((item) => {
      if (item?.notificationCode) {
        codes.add(item.notificationCode)
      }
    })
  })
  const errorDetail = validationResult.responseObject?.errorDetail
  if (Array.isArray(errorDetail)) {
    errorDetail.forEach((item) => {
      if (item?.errorCode) {
        codes.add(item.errorCode)
      }
    })
  }
  return Array.from(codes)
}

export function captureOrderValidateFailure (validationResult) {
  const store = useFlowValidationStore()
  store.orderValidateErrors = extractValidationErrorCodes(validationResult)
}

export function clearOrderValidateFailure () {
  const store = useFlowValidationStore()
  store.orderValidateErrors = []
}

export function isSignupValidationFailed (validationResult) {
  if (!validationResult) {
    return true
  }
  return validationResult.actionStatus === VALIDATION_STATUS.ERROR
}

export async function runPrePaymentSignupValidate (state, appContext, checkoutFlow) {
  if (isCheckoutMockOrderValidateEnabled()) {
    checkoutDebug('Mocking order validate failure')
    clearOrderValidateFailure()
    return false
  }
  const { validateSubscriptions } = useSubscriptions(appContext)
  const payload = buildPrePaymentSignupPayload(state, appContext, checkoutFlow)
  const validationResult = await validateSubscriptions(payload)
  if (isSignupValidationFailed(validationResult)) {
    captureOrderValidateFailure(validationResult)
    return false
  }
  clearOrderValidateFailure()
  return true
}
